import type { AxiosInstance } from 'axios';
import { createAuthenticatedClient } from './authenticatedClient';
import type { NormalizedApiError } from './types';

// Auth wiring is injected so `services/` never imports a feature (one-way dep).
let getAccessToken: (() => string | null) | null = null;
let refreshAccessToken: (() => Promise<string | null>) | null = null;
let onAuthFailure: (() => void) | null = null;
let onAccessBlocked: ((error: NormalizedApiError) => void) | null = null;

export function setAccessTokenResolver(resolver: () => string | null): void {
	getAccessToken = resolver;
}

/**
 * Register the refresh routine. It must resolve to a new access token, or to
 * `null` when the session cannot be renewed (no refresh token / rejected).
 */
export function setAuthRefreshHandler(
	handler: (() => Promise<string | null>) | null,
): void {
	refreshAccessToken = handler;
}

/** Called once the session is no longer recoverable (e.g. clear session). */
export function setOnAuthFailure(handler: (() => void) | null): void {
	onAuthFailure = handler;
}

/** Called when the tenant's CMS IP allowlist refuses a request. */
export function setOnAccessBlocked(
	handler: ((error: NormalizedApiError) => void) | null,
): void {
	onAccessBlocked = handler;
}

/**
 * Single shared HTTP client of the CMS user session. Feature code must use
 * this instead of importing axios directly so interceptors and error handling
 * stay central. The customer portal has its own client (separate session).
 */
const cms = createAuthenticatedClient({
	getAccessToken: () => getAccessToken?.() ?? null,
	refresh: () =>
		refreshAccessToken ? refreshAccessToken() : Promise.resolve(null),
	onAuthFailure: () => onAuthFailure?.(),
	onAccessBlocked: (error) => onAccessBlocked?.(error),
});

export const apiClient: AxiosInstance = cms.client;

/** Thin, typed request helper returning the response body directly. */
export const request = cms.request;

export type { NormalizedApiError };
